import {Collapse, css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  DlMuiToggleFilters,
  GetStylesFunction,
  IconHeaderWithSearch,
  useApiQuery,
  utils,
  locationContainer,
  DateUtils,
  TranslateUtils,
  rawRenderingLib,
  useLocationQuery,
  serializeJSONToString,
  omitNullsy,
  LightSplitLayout,
  LightSplitLayoutProps,
  useLgOrSmaller,
} from '@wallaby-ui/core';
import {
  DateTimeFormatter,
  DocumentNameFormatter,
  DocumentsListDepartmentFormatter,
  LightDateRangeFilter,
  LightSelectedFilters,
  LightSelectFilter,
  LightTextFilter,
  useTableData,
  DEFAULT_PAGE_SIZE,
  MaterialTableWrapper,
  getDefaultMTOptions,
  WallabyCoreSchema,
  clampPageNumber,
  LightOrganizationFilter2,
} from '@wallaby-ui/document';
import AgGrid from 'ag-grid-community';
import {parseISO} from 'date-fns';
import {isEqual} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useCallback, useEffect, useMemo} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {useStore} from 'react-redux';
import {useAppDispatch} from 'src/js/store_hooks';

import {DEFAULT_DOCUMENTS_FILTERS, DEFAULT_DOCUMENTS_SORTING} from './HospitalizationDetailLayoutContainer';
import {HospitalizationDocumentLayoutContainer} from './HospitalizationDocumentLayoutContainer';

const I18N = defineMessages({
  DOCUMENT_NAME_FILTER: {
    id: 'document.filters.documentname',
    defaultMessage: 'Name',
    description: 'Document name',
  },
  ORGANIZATIONS_FILTER: {
    id: 'document.filters.doc_org',
    defaultMessage: 'Organizations',
    description: 'Organizations',
  },
  HOSPITALIZATIONS_FILTER: {
    id: 'document.filters.hospitalization_of_origin',
    defaultMessage: 'Hospitalization of origin',
    description: 'Hospitalization of origin',
  },
  LAST_UPDATE_DATE_FILTER: {
    id: 'document.filters.documentdate',
    defaultMessage: 'Last update date',
    description: 'Last update date',
  },
  DATE_TIME: {
    id: 'documents.list.dateTime',
    defaultMessage: 'Date and time',
    description: 'Datetime column for timeline',
  },
  DOCUMENT_NAME: {
    id: 'documents.list.documentName',
    defaultMessage: 'Document name',
    description: 'Document name column for timeline',
  },
  DOCUMENT_DEPARTMENT: {
    id: 'documents.list.origin',
    defaultMessage: 'Department of origin',
    description: 'Department of origin',
  },
});
type EncounterType = WallabyCoreSchema.components['schemas']['EncounterDetailDto'];
type RowType = WallabyCoreSchema.components['schemas']['DocumentForEncounterDto'];

function getHospitalizationLabel(value: EncounterType, intl: any, store: any) {
  const startedAt = DateUtils.getFormattedDate(parseISO(value.startedAt), intl, true, true);
  const endedAt = value.endedAt
    ? DateUtils.getFormattedDate(parseISO(value.endedAt), intl, true, true)
    : TranslateUtils.translate('patient.card.ongoing', intl);
  const translatedType = rawRenderingLib.translatedEnumValueRenderer(
    {
      enumName: 'EncounterTypeEnum',
      enumValueName: value.type,
    },
    {intl, store}
  );
  return value.type === 'OUTPATIENT'
    ? `${value.organization.name}, ${startedAt} (${translatedType})`
    : `${value.organization.name}, ${startedAt} - ${endedAt} (${translatedType})`;
}

const AG_GRID_COMPATIBILITY: AgGrid.ICellRendererParams = {
  value: undefined,
  valueFormatted: undefined,
  data: undefined,
  node: undefined as unknown as AgGrid.RowNode,
  $scope: undefined,
  rowIndex: -1,
  api: undefined as unknown as AgGrid.GridApi,
  columnApi: undefined as unknown as AgGrid.ColumnApi,
  context: undefined,
  eGridCell: undefined as unknown as HTMLElement,
  eParentOfValue: undefined as unknown as HTMLElement,
  addRenderedRowListener: () => 0,
  registerRowDragger: () => 0,
};

interface OwnProps {
  config: ConfigObjectType;
  hospitalizationId: string;
  documentId: string;
  encounterId: number;
  patientCardId: number;
  areFiltersOpen: {[key: string]: boolean};
  openCloseFilters: (key: 'documents') => void;
  filters: {[key: string]: any};
  haveDefaultFilters: boolean;
  changeFilters: (data: any) => void;
  reuseDocsPage: boolean;
}

type Props = OwnProps;

type PageNumberParams = {
  scope: 'PATIENT_NARROWED_BY_HOSPITALIZATION';
  encounterId: number;
  documentVersionId: string;
  size: number;
};
type LoadDocumentsParams = {
  documentName: string | null;
  documentOrganizationIds: string[] | null;
  documentLastUpdatedFrom: string | undefined | null;
  documentLastUpdatedTo: string | undefined | null;
  hospitalizationsOfOrigin: number[] | null;
  defaultDocumentDateRangeStart: number;
  defaultDocumentDateRangeEnd?: number;
  surgeryProtocolDateRangeStart: number;
} & {
  page: number;
  size: number;
  sort: string | null;
};

function getColumns(filters: Record<string, any>, intl: any): Column<RowType>[] {
  return [
    {
      field: 'date',
      title: utils.TranslateUtils.translate(I18N.DATE_TIME, intl),
      render: (row: RowType) => <DateTimeFormatter {...AG_GRID_COMPATIBILITY} value={parseISO(row.srcCreatedOn)} />,
      width: '127px',
      defaultSort: 'desc',
      customSort(a, b) {
        const key = (v) => parseISO(v.srcCreatedOn);
        const diff = +(key(a) > key(b)) - +(key(a) < key(b));
        return diff;
      },
    },
    {
      field: 'documentName',
      title: utils.TranslateUtils.translate(I18N.DOCUMENT_NAME, intl),
      render: (row: RowType) =>
        row && <DocumentNameFormatter {...AG_GRID_COMPATIBILITY} value={utils.TranslateUtils.translate(row.name, intl)} data={row} />,
      sorting: false,
    },
    {
      field: 'organization',
      title: utils.TranslateUtils.translate(I18N.DOCUMENT_DEPARTMENT, intl),
      render: (row: RowType) =>
        row && <DocumentsListDepartmentFormatter {...AG_GRID_COMPATIBILITY} value={row.organizationShortName} data={row} />,
      width: '90px',
      sorting: false,
    },
  ];
}

export const PatientDocumentsTabPanel: React.FC<Props> = (props) => {
  const {
    config,
    hospitalizationId,
    documentId,
    encounterId,
    patientCardId,
    areFiltersOpen,
    openCloseFilters,
    filters,
    haveDefaultFilters,
    changeFilters,
    reuseDocsPage,
  } = props;
  const intl = useIntl();
  const store = useStore();
  const theme = useTheme();
  const styles = getStyles({theme});

  const hasFilters = useMemo(
    () => !isEqual(omitNullsy(filters.documents), omitNullsy(DEFAULT_DOCUMENTS_FILTERS(encounterId))),
    [filters.documents, encounterId]
  );
  // select first document by default
  const getSorting = (key: string, direction: string | null) => {
    if (direction) return `${key},${direction}`;
    return null;
  };
  const DEFAULT_DOCUMENTS_PARAMS: LoadDocumentsParams = useMemo(
    () => ({
      page: filters.documentsPaging.page ?? 0,
      size: filters.documentsPaging.pageSize ?? DEFAULT_PAGE_SIZE,
      sort: getSorting('srcCreatedOn', filters.documentsSorting.date),
      documentName: filters.documents.documentName,
      documentOrganizationIds: filters.documents.organizationIds,
      documentLastUpdatedFrom: filters.documents.dateRange?.startDate,
      documentLastUpdatedTo: filters.documents.dateRange?.endDate,
      documentShortcut: filters.documents.dateRange?.shortcut,
      hospitalizationsOfOrigin: filters.documents.hospitalizationIds,
      defaultDocumentDateRangeStart: 365,
      defaultDocumentDateRangeEnd: 365,
      surgeryProtocolDateRangeStart: 365,
    }),
    [haveDefaultFilters] // eslint-disable-line
  );
  const firstPageData = useTableData<LoadDocumentsParams, RowType[]>(
    `${config.service.url.documents}${encounterId}`,
    DEFAULT_DOCUMENTS_PARAMS,
    'content',
    haveDefaultFilters
  );
  const dispatch = useAppDispatch();
  const storageIdPrefix = `/hospitalizedPatients/${hospitalizationId}/`;
  const [query] = useLocationQuery(storageIdPrefix);
  const navigateTo = useCallback(
    (route: string, params) => {
      dispatch(locationContainer.navigateToRouteWithParams(locationContainer.LOCATION_REDUCER_STATE_ID, route, params));
    },
    [dispatch]
  );
  useEffect(() => {
    if (firstPageData.data?.[0]?.documentVersionId && !documentId) {
      const firstDocumentId = firstPageData.data[0].documentVersionId;
      navigateTo(`/hospitalizedPatients/${hospitalizationId}/document/${firstDocumentId}`, query);
    }
  }, [firstPageData, navigateTo, hospitalizationId, documentId, query]);

  // hospitalizations
  const hospitalizationsUrl = `${config.service.url.patientsRoot}/${patientCardId}/encounters`;
  const {data: hospitalizationsData, isLoading: hospitalizationsDataIsLoading} = useApiQuery<EncounterType[]>(
    [hospitalizationsUrl, patientCardId],
    null,
    {axiosConfig: {url: hospitalizationsUrl}}
  );

  // documents
  const apiParams: LoadDocumentsParams = useMemo(() => {
    return {
      ...DEFAULT_DOCUMENTS_PARAMS,
      page: filters.documentsPaging.page ?? 0,
      size: filters.documentsPaging.pageSize ?? DEFAULT_PAGE_SIZE,
      sort: getSorting('srcCreatedOn', filters.documentsSorting.date),
      documentName: filters.documents.documentName,
      documentOrganizationIds: filters.documents.organizationIds,
      documentLastUpdatedFrom: filters.documents.dateRange?.startDate,
      documentLastUpdatedTo: filters.documents.dateRange?.endDate,
      hospitalizationsOfOrigin: filters.documents.hospitalizationIds,
    };
  }, [DEFAULT_DOCUMENTS_PARAMS, filters.documentsPaging, filters.documentsSorting, filters.documents]);
  const documentsData = useTableData<LoadDocumentsParams, RowType[]>(
    `${config.service.url.documents}${encounterId}`,
    apiParams,
    'content',
    haveDefaultFilters
  );

  // set page number from documentId
  const needDefaultPageNumber = !reuseDocsPage || filters.documentsPaging.page == null;
  const pageNumberUrl = `${config.service.url.documents}${encounterId}/document-version/${documentId}/page-index`;
  const pageNumberApiParams: PageNumberParams = useMemo(
    () => ({
      scope: 'PATIENT_NARROWED_BY_HOSPITALIZATION',
      encounterId,
      documentVersionId: documentId,
      size: filters.documentsPaging.pageSize ?? DEFAULT_PAGE_SIZE,
    }),
    [encounterId, documentId, filters.documentsPaging.pageSize]
  );
  const {data: defaultPageNumber} = useApiQuery<number>([pageNumberUrl, serializeJSONToString(pageNumberApiParams)], null, {
    axiosConfig: {
      url: pageNumberUrl,
      params: new URLSearchParams(pageNumberApiParams as any),
    },
    reactQueryOptions: {
      enabled: needDefaultPageNumber && Boolean(documentId),
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });
  useEffect(() => {
    if (defaultPageNumber === undefined) return;
    setTimeout(() => {
      // NOTE: run after table callback
      changeFilters({
        documentsPaging: {
          ...filters.documentsPaging,
          page: defaultPageNumber,
        },
      });
    }, 0);
    // eslint-disable-next-line
  }, [defaultPageNumber]);

  const isLoading = documentsData.isLoading || hospitalizationsDataIsLoading;
  const leftLayout = (
    <div data-name={'patient-documents-tab-panel--left-layout'} css={styles.leftLayout}>
      <IconHeaderWithSearch iconName={'documentsIcon'} label={'hospitalization.detail.documentation'}>
        <DlMuiToggleFilters
          title={areFiltersOpen.documents ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
          onClick={() => openCloseFilters('documents')}
          areFiltersOpen={areFiltersOpen.documents}
        />
      </IconHeaderWithSearch>
      <Collapse
        in={areFiltersOpen.documents}
        timeout={500}
        style={{
          minHeight: areFiltersOpen.documents ? 'unset' : 0,
        }}
      >
        <div css={styles.filteringContainerWrapper}>
          <LightTextFilter
            config={{
              name: 'documentName',
              ui: {label: I18N.DOCUMENT_NAME_FILTER.id},
            }}
            value={filters.documents.documentName}
            onChange={(newValue) =>
              changeFilters({
                documents: {
                  ...filters.documents,
                  documentName: newValue,
                },
                documentsPaging: {
                  ...filters.documentsPaging,
                  page: 0,
                },
              })
            }
          />
          <LightDateRangeFilter
            config={{
              name: 'dateRange',
              ui: {label: I18N.LAST_UPDATE_DATE_FILTER.id},
            }}
            value={
              filters.documents.dateRange?.startDate && filters.documents.dateRange?.endDate
                ? {
                    startDate: filters.documents.dateRange.startDate,
                    endDate: filters.documents.dateRange.endDate,
                    shortcut: filters.documents.dateRange.shortcut,
                  }
                : null
            }
            onChange={(newValue) => {
              changeFilters({
                documents: {
                  ...filters.documents,
                  dateRange: newValue,
                },
                documentsPaging: {
                  ...filters.documentsPaging,
                  page: 0,
                },
              });
            }}
            dateOnly
            showShortcuts
          />
          <LightOrganizationFilter2
            appConfig={config}
            config={{
              name: 'organizationIds',
              ui: {label: I18N.ORGANIZATIONS_FILTER.id},
            }}
            multi
            value={filters.documents.organizationIds}
            onChange={(newOrganizationIds) => {
              changeFilters({
                documents: {
                  ...filters.documents,
                  organizationIds: newOrganizationIds,
                },
                documentsPaging: {
                  ...filters.documentsPaging,
                  page: 0,
                },
              });
            }}
          />
          <LightSelectFilter
            config={{
              name: 'hospitalizationIds',
              ui: {label: I18N.HOSPITALIZATIONS_FILTER.id},
            }}
            options={(hospitalizationsData ?? []).map((v) => ({
              id: v.id,
              label: getHospitalizationLabel(v, intl, store),
            }))}
            value={filters.documents.hospitalizationIds}
            onChange={(newHospitalizationIds) => {
              changeFilters({
                documents: {
                  ...filters.documents,
                  hospitalizationIds: newHospitalizationIds,
                },
                documentsPaging: {
                  ...filters.documentsPaging,
                  page: 0,
                },
              });
            }}
            multi
          />
        </div>
      </Collapse>
      <LightSelectedFilters
        hasFilters={hasFilters}
        handleResetFilters={() => {
          changeFilters({
            documents: DEFAULT_DOCUMENTS_FILTERS(encounterId),
            documentsPaging: {
              ...filters.documentsPaging,
              page: 0,
            },
          });
        }}
        hasSorting={!isEqual(filters.documentsSorting, DEFAULT_DOCUMENTS_SORTING)}
        handleResetSorting={() => {
          changeFilters({
            documentsSorting: DEFAULT_DOCUMENTS_SORTING,
          });
        }}
      />
      <MaterialTableWrapper
        variant='big'
        paginationOptions={{
          showPageSize: false,
          showResultCount: false,
          showMoveToPage: false,
          maxPageClickers: 2,
        }}
        getRowClass={(rowData, index) => {
          if (documentId && rowData.documentVersionId === +documentId) {
            return 'dlAgGridPreSelectedRow';
          }
          return index % 2 === 0 ? 'ag-row-even' : 'ag-row-odd';
        }}
        options={getDefaultMTOptions(documentsData.countOnPage ?? DEFAULT_PAGE_SIZE)}
        useMaxHeight
        isLoading={isLoading}
        columns={getColumns(filters, intl)}
        data={documentsData?.data ?? []}
        totalCount={documentsData?.totalCount}
        page={documentsData?.page}
        totalPages={documentsData?.totalPages}
        onChangePage={(page, pageSize) =>
          changeFilters({
            documentsPaging: {
              ...filters.documentsPaging,
              page,
              pageSize,
            },
          })
        }
        onChangeRowsPerPage={(pageSize) =>
          changeFilters({
            documentsPaging: {
              ...filters.documentsPaging,
              pageSize,
              page: clampPageNumber(filters.documentsPaging.page, pageSize, documentsData.totalCount),
            },
          })
        }
        onOrderChange={(colIndex, direction) => {
          const columnId = getColumns(filters, intl)[colIndex].field as string;
          changeFilters({
            documentsSorting: {
              [columnId]: direction,
            },
          });
        }}
        controlledSort={(() => {
          return {
            field: 'date',
            direction: filters.documentsSorting.date,
          };
        })()}
        controlledPageSize={filters.documentsPaging.pageSize ?? DEFAULT_PAGE_SIZE}
        scrollStorageId={`${storageIdPrefix}.documentsScrollXY`}
      />
    </div>
  );
  const rightLayout = (
    <div data-name={'patient-documents-tab-panel--right-layout'} css={styles.rightLayout}>
      <HospitalizationDocumentLayoutContainer config={config} storageIdPrefix={`${storageIdPrefix}/document/${documentId}`} />
    </div>
  );
  const anyPatientStorageIdPrefix = '/hospitalizedPatients/any';
  const smallSplit: LightSplitLayoutProps = {
    initSizes: [20, 20],
    minSizes: [82, 82],
    storageId: `${anyPatientStorageIdPrefix}.smallSplit`,
    isVertical: true,
  };
  const bigSplit: LightSplitLayoutProps = {
    initSizes: [39, 61],
    minSizes: [260, 200],
    storageId: `${anyPatientStorageIdPrefix}.bigSplit`,
    isVertical: false,
  };
  const lgOrSmaller = useLgOrSmaller();
  return lgOrSmaller ? (
    <LightSplitLayout key='smallSplit' {...smallSplit}>
      {leftLayout}
      {rightLayout}
    </LightSplitLayout>
  ) : (
    <LightSplitLayout key='bigSplit' {...bigSplit}>
      {leftLayout}
      {rightLayout}
    </LightSplitLayout>
  );
};

PatientDocumentsTabPanel.displayName = 'PatientDocumentsTabPanel';

const getStyles: GetStylesFunction = (options) => ({
  leftLayout: css`
    background: #fff;
    padding: 12px;
    border-radius: 24px;
    display: flex;
    flex-direction: column;
    row-gap: 4px;
  `,
  tableWrapper: css`
    flex: 1;
  `,
  rightLayout: css``,
  filteringContainerWrapper: css`
    width: 100%;
    display: flex;
    column-gap: 10px;
    align-items: start;
    flex-wrap: wrap;
  `,
});
